import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Scale, FileText, Sparkles, ShieldCheck, Gavel } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950 text-white">
      {/* Left: brand / pitch */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/90 to-slate-900/70" />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png" alt="Logo do escritório" className="h-14 w-14 rounded-xl object-cover ring-1 ring-amber-500/40" />
          <span className="font-display text-2xl font-extrabold tracking-tight" data-testid="login-brand-title">ASSESSORIA JURÍDICA SILVESTRE&amp;MAIA - IA</span>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.2em] mb-4">
            Assessoria Jurídica Trabalhista
          </p>
          <h1 className="font-display text-4xl font-extrabold leading-tight mb-6">
            Da ficha de atendimento à petição protocolável.
          </h1>
          <ul className="space-y-4 text-slate-300">
            <li className="flex gap-3"><FileText className="h-5 w-5 text-amber-500 shrink-0" /> Extração automática de dados com IA e revisão campo a campo.</li>
            <li className="flex gap-3"><Gavel className="h-5 w-5 text-amber-500 shrink-0" /> Fundamentação com Súmulas e OJs reais do TST.</li>
            <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-amber-500 shrink-0" /> Alertas de risco para dados faltantes antes de exportar.</li>
          </ul>
        </div>
        <div className="relative z-10 text-slate-500 text-xs">Uso interno — acesso restrito aos advogados do escritório.</div>
      </div>

      {/* Right: login */}
      <div className="flex items-center justify-center p-8 bg-slate-950">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <img src="/logo.png" alt="Logo do escritório" className="h-12 w-12 rounded-xl object-cover ring-1 ring-amber-500/40" />
            <span className="font-display text-lg font-extrabold text-center leading-tight" data-testid="login-brand-title-mobile">Silvestre&amp;Maia - IA</span>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-2 text-amber-500 mb-2">
              <Sparkles className="h-4 w-4" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Bem-vindo</span>
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Acessar a plataforma</h2>
            <p className="text-slate-400 text-sm mb-8">
              Entre com a conta Google autorizada do escritório para continuar.
            </p>
            <Button
              data-testid="google-login-btn"
              onClick={login}
              className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 font-semibold gap-3"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
              </svg>
              Entrar com Google
            </Button>
            <p className="text-slate-500 text-xs mt-6 text-center leading-relaxed">
              Dados protegidos e acesso autenticado conforme a LGPD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
